import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

/**
 * Context provider that enables child routes to toggle full-width content layout.
 *
 * Wrap a subtree with this provider so that descendant components can call
 * {@link useWideContent} or {@link useWideContentValue} to control or read
 * the wide-content state.
 */
export function WideContentProvider({ children }: { children: ReactNode }) {
  const [wideContent, setWideContent] = useState(false)

  const value = useMemo(
    () => ({ wideContent, setWideContent }),
    [wideContent, setWideContent]
  )

  return (
    <WideContentContext.Provider value={value}>
      {children}
    </WideContentContext.Provider>
  )
}

/**
 * Hook that opts the current route into full-width content (no max-width constraint).
 *
 * NOTE: This assumes a single active consumer at a time.
 */
export function useWideContent() {
  const context = useContext(WideContentContext)
  const setWideContent = context?.setWideContent
  useEffect(() => {
    if (!setWideContent) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn(
          'useWideContent: no WideContentProvider found. Wide content will not be applied.'
        )
      }
      return
    }
    setWideContent(true)
    return () => setWideContent(false)
  }, [setWideContent])
}

/** Hook to read the current wide-content state. */
export function useWideContentValue() {
  return useWideContentContext().wideContent
}

/** Value provided by {@link WideContentProvider}. */
interface WideContentContextValue {
  /** Whether the content area is currently in full-width mode. */
  wideContent: boolean
  /** Sets the full-width mode on or off. */
  setWideContent: (wide: boolean) => void
}

const WideContentContext = createContext<WideContentContextValue | null>(null)

function useWideContentContext() {
  const value = useContext(WideContentContext)
  if (!value) {
    throw new Error(
      'useWideContent/useWideContentValue must be used within a WideContentProvider'
    )
  }
  return value
}
